import { enqueueSnackbar } from 'notistack';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import {
  getCandidateDetailsById,
  getCandidateDetailsByJobId,
  getPreScreeningQuestions,
} from 'src/services/candidate.service';
import PropTypes from 'prop-types';
import { useErrorBoundary } from 'react-error-boundary';
import { AiInterviewState, ProfileState } from '../constants/constants-nav';

export const CandidateProfileContext = createContext();

export const CandidateProfileProvider = ({
  candidateId,
  candidateType = 'JOB_CANDIDATE',
  children,
  token,
}) => {
  // states
  const [navigationItems, setNavigationItems] = useState(['Profile']);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [orgId, setOrgId] = useState('');
  const [userId, setUserId] = useState('');
  const [jobId, setJobId] = useState('');
  const [trigger, setTrigger] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [navGroup, setNavGroup] = useState('');

  // hooks
  const { showBoundary } = useErrorBoundary();

  // effects
  useEffect(() => {
    const orgDetailsCaller = async () => {
      if (!candidateId || !token) return;

      const publicCandidateOrgData = await getCandidateDetailsById(
        candidateId,
        token,
      );

      if (publicCandidateOrgData.success) {
        const _orgId = publicCandidateOrgData.body.organization_id;
        const _userId = publicCandidateOrgData.body.user_id;
        const _jobId = publicCandidateOrgData.body.job_opening_id;

        setOrgId(_orgId);
        setUserId(_userId);
        setJobId(_jobId);
      } else {
        enqueueSnackbar('Cannot fetch candidate!');
        throw new Error(publicCandidateOrgData.message);
      }
    };

    orgDetailsCaller();
  }, [candidateId, token]);

  useEffect(() => {
    const caller = async () => {
      if (!orgId || !jobId || !userId || !token) return;

      try {
        const publicCandidateData = await getCandidateDetailsByJobId(
          orgId,
          jobId,
          userId,
          token,
        );

        if (publicCandidateData.success) {
          setCandidateDetails(publicCandidateData.body);

          const _interview = publicCandidateData.body.interview_details;

          const isInterviewExist =
            _interview && _interview.interview_status !== 'UNATTEMPTED';

          let isPreScreeningExist = false;

          let hasSolutions = false;
          let isEvaluated = false;
          let preScreeningData = [];

          const preScreeningResponse = await getPreScreeningQuestions(
            jobId,
            publicCandidateData.body.candidate_info.user_id,
            token,
          );

          if (preScreeningResponse.success) {
            const _prescreeningQuestions =
              preScreeningResponse?.body?.questions?.map(item => {
                const question = {
                  questionId: item?.question_id,
                  responseParameters: item?.response_parameters,
                  question: item?.text,
                  isQualifyingQuestion: item?.is_qualifying_question,
                  responseType: item?.response_type,
                  response: null,
                  title: item?.title,
                };

                return question;
              });

            preScreeningResponse?.body?.solutions?.forEach(
              (solution, index) => {
                const questionObj = _prescreeningQuestions.find(
                  question => question.questionId === solution.question_id,
                );

                if (questionObj) {
                  questionObj.response = {
                    responseText: solution?.response_text,
                    isEvaluated: solution?.is_evaluated,
                    success: solution?.score === 1,
                  };
                }
              },
            );

            hasSolutions = preScreeningResponse?.body?.solutions?.length > 0;
            isEvaluated = preScreeningResponse?.body?.is_evaluated;
            preScreeningData = _prescreeningQuestions;

            if (
              !preScreeningData ||
              preScreeningData?.length === 0 ||
              !hasSolutions ||
              isEvaluated === null
            ) {
              isPreScreeningExist = false;
            } else {
              isPreScreeningExist = true;
            }
          }

          if (isPreScreeningExist) {
            setNavigationItems(prev => [...prev, 'Pre-screening']);
          }

          if (_interview && isInterviewExist) {
            setNavigationItems(prev => [...prev, 'AI interview']);
            setNavGroup(AiInterviewState);
          } else {
            setNavGroup(ProfileState);
          }
        } else {
          enqueueSnackbar('Cannot fetch candidate!');
          throw new Error(publicCandidateData.message);
        }
      } catch (err) {
        console.log(err);
        showBoundary(err);
      } finally {
        setIsLoading(false);
      }
    };

    caller();
  }, [candidateId, jobId, orgId, showBoundary, token, trigger, userId]);

  const contextObj = useMemo(() => {
    const obj = {
      obj: candidateDetails,
      saveChanges: () => {
        setTrigger(prev => !prev);
      },
      isLoading,
      candidateType,
      token,
      orgId,
      userId,
      jobId,
      navGroup,
      navigationItems,
      setNavGroup,
    };
    return obj;
  }, [
    candidateDetails,
    candidateType,
    isLoading,
    jobId,
    orgId,
    token,
    userId,
    navGroup,
    navigationItems,
    setNavGroup,
  ]);

  return (
    <CandidateProfileContext.Provider value={contextObj}>
      {children}
    </CandidateProfileContext.Provider>
  );
};

CandidateProfileProvider.displayName = 'CandidateProfileProvider';

CandidateProfileProvider.propTypes = {
  children: PropTypes.node,
  candidateId: PropTypes.string,
  candidateType: PropTypes.string,
  token: PropTypes.string,
};
